import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, Lock, Copy, Fingerprint } from "lucide-react";
import { toast } from "sonner";
import { startAuthentication } from "@simplewebauthn/browser";
import { api, errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

function Shell({ children, title, sub }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#14213D] text-white p-14">
        <span className="font-signature text-4xl md:text-5xl leading-tight text-center">WorkSpace &amp; Admin Panel</span>
        <div>
          <ShieldCheck size={40} className="text-[#C5A059] mb-6" />
          <p className="font-serif text-3xl leading-snug max-w-md">Sitenizin tüm içeriği, güvenli ve iki adımlı doğrulamalı bir panelden yönetilir.</p>
        </div>
        <p className="text-sm text-white/50">Oturumlar şifrelenir, şifreniz tarayıcıda saklanmaz.</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <h1 className="font-serif text-3xl font-bold">{title}</h1>
          <p className="text-stone-500 mt-2 mb-8 text-sm">{sub}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

function Setup2FA({ token, onDone }) {
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const start = async () => {
    try { setData((await api.post("/auth/2fa/setup", null, auth)).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setResult((await api.post("/auth/2fa/enable", { code }, auth)).data); } catch (er) { toast.error(errMsg(er)); }
    setBusy(false);
  };
  if (result)
    return (
      <Shell title="Yedek kodlarınız" sub="Telefonunuza erişemezseniz bu kodlarla giriş yapabilirsiniz. Her kod bir kez kullanılır; güvenli bir yere kaydedin.">
        <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-white border rounded-xl p-4" data-testid="backup-codes">
          {result.backup_codes.map((c) => <span key={c}>{c}</span>)}
        </div>
        <Button variant="outline" className="w-full mt-3" onClick={() => navigator.clipboard?.writeText(result.backup_codes.join("\n"))} data-testid="copy-backup-codes"><Copy size={15} className="mr-2" />Kopyala</Button>
        <Button className="w-full mt-3" onClick={() => onDone(result)} data-testid="finish-2fa-setup">Kaydettim, panele geç</Button>
      </Shell>
    );
  return (
    <Shell title="2 adımlı doğrulama kurulumu" sub="İlk girişte Google Authenticator kurulumu zorunludur.">
      {!data ? (
        <Button className="w-full" onClick={start} data-testid="start-2fa-setup">Kurulumu başlat</Button>
      ) : (
        <form onSubmit={enable} className="space-y-4">
          <div className="bg-white border rounded-xl p-5 flex flex-col items-center gap-3">
            <QRCodeSVG value={data.otpauth_uri} size={180} data-testid="totp-qr" />
            <p className="text-xs text-stone-500 text-center">Uygulamada QR kodu okutun veya anahtarı elle girin:</p>
            <code className="text-xs break-all bg-stone-100 px-2 py-1 rounded" data-testid="totp-secret">{data.secret}</code>
          </div>
          <div>
            <Label>Uygulamadaki 6 haneli kod</Label>
            <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="setup-2fa-code" className="mt-1.5 tracking-[0.4em] text-center text-lg" autoComplete="one-time-code" />
          </div>
          <Button className="w-full" disabled={busy || code.length !== 6} data-testid="enable-2fa-submit">Doğrula ve etkinleştir</Button>
        </form>
      )}
    </Shell>
  );
}

export default function Login({ onLogin }) {
  const [f, setF] = useState({ email: "", password: "", code: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [setupToken, setSetupToken] = useState(null);
  const [pwReadonly, setPwReadonly] = useState(true);
  const [passkeyAvail, setPasskeyAvail] = useState(false);
  const [pkBusy, setPkBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (!window.PublicKeyCredential) return;
        const { data } = await api.get("/public/auth/passkey/available");
        setPasskeyAvail(!!data?.enabled);
      } catch { /* ignore */ }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", f);
      setF({ email: f.email, password: "", code: "" });
      if (data.setup_required) setSetupToken(data.setup_token);
      else onLogin(data);
    } catch (er) {
      setError(errMsg(er));
      setF({ ...f, password: "", code: "" });
    }
    setBusy(false);
  };

  const loginWithPasskey = async () => {
    if (pkBusy) return;
    setPkBusy(true); setError("");
    try {
      const begin = await api.post("/public/auth/passkey/begin", { email: f.email || null }).then((r) => r.data);
      const ceremonyId = begin.ceremonyId;
      const options = { ...begin };
      delete options.ceremonyId;
      const assertion = await startAuthentication({ optionsJSON: options });
      const { data } = await api.post("/public/auth/passkey/complete", assertion, { headers: { "X-Ceremony-Id": ceremonyId } });
      onLogin(data);
    } catch (er) {
      if (er?.name === "NotAllowedError") setError("İşlem iptal edildi veya zaman aşımına uğradı.");
      else setError(errMsg(er));
    } finally { setPkBusy(false); }
  };

  if (setupToken) return <Setup2FA token={setupToken} onDone={onLogin} />;
  return (
    <Shell title="Panele giriş" sub="Parmak izi / Face ID ile tek dokunuşla giriş yapabilir ya da şifre + 2FA kullanabilirsiniz.">
      {passkeyAvail && (
        <>
          <Button type="button" variant="outline" className="w-full" onClick={loginWithPasskey} disabled={pkBusy} data-testid="passkey-login-btn">
            <Fingerprint size={16} className="mr-2" />{pkBusy ? "Doğrulanıyor…" : "Parmak izi / Face ID ile giriş"}
          </Button>
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center" aria-hidden><div className="w-full border-t border-stone-200" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-stone-400 uppercase tracking-widest">veya şifre ile</span></div>
          </div>
        </>
      )}
      <form onSubmit={submit} className="space-y-4" autoComplete="off" data-testid="admin-login-form">
        <div><Label>E-posta</Label><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5" data-testid="admin-login-email" autoComplete="off" /></div>
        <div><Label>Şifre</Label><Input type="password" required value={f.password} readOnly={pwReadonly} onFocus={() => setPwReadonly(false)} onChange={(e) => setF({ ...f, password: e.target.value })} className="mt-1.5" data-testid="admin-login-password" autoComplete="new-password" /></div>
        <div>
          <Label>Doğrulama kodu (2FA, açıksa)</Label>
          <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.trim() })} maxLength={12} placeholder="123456 veya yedek kod" className="mt-1.5 tracking-widest" data-testid="admin-login-2fa-code" autoComplete="one-time-code" />
          <p className="text-xs text-stone-400 mt-1">2 adımlı doğrulama kapalıysa boş bırakın.</p>
        </div>
        {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
        <Button className="w-full" disabled={busy} data-testid="admin-login-submit"><Lock size={15} className="mr-2" />{busy ? "Kontrol ediliyor…" : "Giriş yap"}</Button>
      </form>
    </Shell>
  );
}
